import type { CSSProperties } from 'react'
import React from 'react'

import type { RadiusScale } from '@gousto-internal/citrus-react'
import {
  Body1,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Body2,
  Image,
  useTheme,
  Display,
} from '@gousto-internal/citrus-react'

import { RecipeSubtitle } from '@features/recipe-tile/src/components/RecipeSubtitle'
import { isCaloriePreferenceSelected } from '@library/api-menu-experience'
import type { MenuPreferences } from '@library/api-menu-experience'
import type { Recipe } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import type { AddRecipeByUuidFn, RemoveRecipeByUuidFn } from 'routes/Menu/domains/basket'

import type { TrackCTAClick } from '../types'
import { InvalidPreferenceTag } from './InvalidPreferenceTag'

const IMAGE_WIDTH = 200

export type SearchRecipeTileProps = {
  addRecipe: AddRecipeByUuidFn
  menuPreferences?: MenuPreferences
  onClick: () => void
  reachedLimit: boolean
  recipe: Recipe
  removeRecipe: RemoveRecipeByUuidFn
  showCustomisedTileFooter?: boolean
  trackingData: {
    invocationId?: string
    menuId: string
    index?: number
  }
}

const SearchRecipeTile = ({
  addRecipe,
  menuPreferences,
  onClick,
  reachedLimit,
  recipe,
  removeRecipe,
  trackingData,
  showCustomisedTileFooter = false,
}: SearchRecipeTileProps) => {
  const { theme } = useTheme()
  const { numPortions } = useNumPortions()
  const recipeImage = recipe.images?.[0]?.crops?.find(({ width }) => width === IMAGE_WIDTH) ?? null

  const { basketHasRecipe } = useBasketRecipes()
  const isInBasket = basketHasRecipe(recipe.id)

  const trackingProperties: TrackCTAClick['properties'] = {
    search_invocation_id: trackingData.invocationId,
    search_rank: trackingData.index,
    menu_id: trackingData.menuId,
    add_type: 'search',
    event_screen: 'view_recipe_detail_screen',
    recipe_id: recipe.id,
  }

  const onClickCTA = (event: React.SyntheticEvent) => {
    event.stopPropagation()
    if (isInBasket) {
      removeRecipe(recipe.id)
      queueTrackingEvent<TrackCTAClick>({
        action: 'remove_recipe',
        properties: trackingProperties,
      })
    } else {
      addRecipe(recipe.id)
      queueTrackingEvent<TrackCTAClick>({
        action: 'add_recipe',
        properties: trackingProperties,
      })
    }
  }

  const onKeyDown = (
    event: React.KeyboardEvent,
    callback: (event: React.KeyboardEvent) => void,
  ) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.stopPropagation()
      event.preventDefault()
      callback(event)
    }
  }

  return (
    <Box
      aria-label="View recipe details"
      display={Display.Flex}
      onClick={onClick}
      onKeyDown={(event) => onKeyDown(event, onClick)}
      role="button"
      style={{ cursor: 'pointer' }}
      tabIndex={0}
    >
      <Box borderRadius={1}>
        {recipeImage ? (
          <Image
            alt={recipe.name}
            src={recipeImage.url}
            width="120px"
            height="120px"
            style={{ ...(theme.radius(2 as RadiusScale) as CSSProperties) }}
            data-testid="search-recipe-tile"
          />
        ) : (
          <Box width="120px" height="120px" />
        )}
      </Box>
      <Box flexGrow={1} paddingH={4}>
        <Body1 fontWeight={700} color={Color.ColdGrey_800}>
          {recipe.name}
        </Body1>
        <Box display={Display.Flex}>
          {showCustomisedTileFooter ? (
            <RecipeSubtitle
              recipe={recipe}
              menuPreferences={menuPreferences}
              numPortions={numPortions}
            />
          ) : (
            <>
              <Body2>{`${recipe.prepTime} mins`}</Body2>
              {isCaloriePreferenceSelected(menuPreferences?.display?.list) && (
                <Body2>
                  &nbsp;&nbsp;•&nbsp;&nbsp;
                  {`${recipe.nutritionalInformation.perPortion.energyKcal}kcal`}
                </Body2>
              )}
            </>
          )}
        </Box>
        {recipe.invalidPreference && (
          <Box paddingV={1}>
            <InvalidPreferenceTag text={recipe.invalidPreference} />
          </Box>
        )}
      </Box>
      <Box>
        <Button
          variant={ButtonVariant.Icon}
          colorVariant={isInBasket ? ButtonColorVariant.Secondary : ButtonColorVariant.Primary}
          icon={isInBasket ? 'remove' : 'add'}
          aria-label={isInBasket ? 'Remove recipe' : 'Add recipe'}
          onClick={onClickCTA}
          onKeyDown={(event) => onKeyDown(event, onClickCTA)}
          tabIndex={0}
          disabled={reachedLimit && !isInBasket}
        />
      </Box>
    </Box>
  )
}

export { SearchRecipeTile }
